import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from './AuthContext';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'mysms360.theme';
const STORAGE_KEY_BRAND = 'mysms360.brand';
const VALID = ['light', 'dark', 'system'];
const VALID_BRANDS = ['blue', 'green', 'orange'];
const DEFAULT_BRAND = 'blue';

const readStored = () => {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return VALID.includes(v) ? v : 'system';
};

const readStoredBrand = () => {
  if (typeof window === 'undefined') return DEFAULT_BRAND;
  const v = window.localStorage.getItem(STORAGE_KEY_BRAND);
  return VALID_BRANDS.includes(v) ? v : DEFAULT_BRAND;
};

// Guard hard: some environments (privacy modes, embedded webviews, older
// browsers) have `window` but no `window.matchMedia`. Calling it then
// throws and — because ThemeProvider wraps the whole app — crashes every
// route into the error boundary. Treat any failure as 'light'.
const resolveSystem = () => {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  } catch (_) {
    /* matchMedia unavailable / threw — fall through to light */
  }
  return 'light';
};

const applyClass = (effective) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (effective === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  root.style.colorScheme = effective;
};

const applyBrand = (brand) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-brand', brand);
};

export const ThemeProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [theme, setThemeState] = useState(readStored);
  const [brand, setBrandState] = useState(readStoredBrand);

  const effective = theme === 'system' ? resolveSystem() : theme;

  // Apply the class whenever the theme (or system pref while in 'system' mode) changes.
  useEffect(() => {
    applyClass(effective);
  }, [effective]);

  // Apply the brand data-attribute whenever it changes.
  useEffect(() => {
    applyBrand(brand);
  }, [brand]);

  // Watch the OS preference while we're tracking it.
  useEffect(() => {
    if (
      theme !== 'system' ||
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    )
      return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyClass(resolveSystem());
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [theme]);

  // On login, hydrate from the user record (server is the source of truth across devices).
  useEffect(() => {
    if (!isAuthenticated() || !user) return;
    const serverPref = user.theme_preference;
    if (serverPref && VALID.includes(serverPref) && serverPref !== readStored()) {
      window.localStorage.setItem(STORAGE_KEY, serverPref);
      setThemeState(serverPref);
    }
    const serverBrand = user.brand_preference;
    if (serverBrand && VALID_BRANDS.includes(serverBrand) && serverBrand !== readStoredBrand()) {
      window.localStorage.setItem(STORAGE_KEY_BRAND, serverBrand);
      setBrandState(serverBrand);
    }
  }, [user, isAuthenticated]);

  const setTheme = useCallback(
    (next) => {
      if (!VALID.includes(next)) return;
      window.localStorage.setItem(STORAGE_KEY, next);
      setThemeState(next);
      // Fire-and-forget persist to the user record so it follows them across devices.
      if (isAuthenticated()) {
        apiClient
          .patch('/api/auth/me/preferences', { theme_preference: next })
          .catch(() => {});
      }
    },
    [isAuthenticated],
  );

  const setBrand = useCallback(
    (next) => {
      if (!VALID_BRANDS.includes(next)) return;
      window.localStorage.setItem(STORAGE_KEY_BRAND, next);
      setBrandState(next);
      // Fire-and-forget persist. Backend may not yet accept this field;
      // localStorage carries state until the route lands.
      if (isAuthenticated()) {
        apiClient
          .patch('/api/auth/me/preferences', { brand_preference: next })
          .catch(() => {});
      }
    },
    [isAuthenticated],
  );

  const cycle = useCallback(() => {
    const order = ['light', 'dark', 'system'];
    const idx = order.indexOf(theme);
    setTheme(order[(idx + 1) % order.length]);
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, effective, setTheme, cycle, brand, setBrand }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};

export default ThemeContext;
